// Permits domain constants and labels
export const PERMIT_TYPES = [
  {
    id: 'autorisation_travail',
    label: "Autorisation de Travail",
    short: 'AT',
    description: "Permis général pour tout travail nécessitant une autorisation préalable",
    color: 'bg-blue-50 border-blue-200 text-blue-900',
  },
  {
    id: 'permis_feu',
    label: "Permis de Feu",
    short: 'PF',
    description: "Travaux par point chaud (soudage, oxycoupage, meulage…)",
    color: 'bg-orange-50 border-orange-200 text-orange-900',
  },
  {
    id: 'permis_levage',
    label: "Permis de Levage",
    short: 'PL',
    description: "Opérations de levage > 3 mètres ou charge critique",
    color: 'bg-amber-50 border-amber-200 text-amber-900',
  },
  {
    id: 'permis_hauteur',
    label: "Permis de Travail en Hauteur",
    short: 'TH',
    description: "Travaux ≥ 1,80 m avec risque de chute (harnais obligatoire)",
    color: 'bg-violet-50 border-violet-200 text-violet-900',
  },
  {
    id: 'permis_nacelle',
    label: "Permis Nacelle",
    short: 'PN',
    description: "Travail en nacelle ciseaux, télescopique ou panier",
    color: 'bg-cyan-50 border-cyan-200 text-cyan-900',
  },
  {
    id: 'permis_espace_confine',
    label: "Permis d'Entrée en Espace Confiné",
    short: 'EC',
    description: "Accès à un espace confiné non déclassé (atmosphère, asphyxie...)",
    color: 'bg-rose-50 border-rose-200 text-rose-900',
  },
  {
    id: 'permis_fouille',
    label: "Permis de Fouille",
    short: 'FO',
    description: "Travaux de terrassement, excavation, tranchée",
    color: 'bg-yellow-50 border-yellow-200 text-yellow-900',
  },
  {
    id: 'permis_bordure_eau',
    label: "Permis Travail en Bordure d'Eau",
    short: 'BE',
    description: "Travaux à proximité d'un point d'eau (risque noyade)",
    color: 'bg-sky-50 border-sky-200 text-sky-900',
  },
  {
    id: 'attestation_consignation_elec',
    label: "Attestation Consignation Électrique",
    short: 'CE',
    description: "Consignation électrique NFC 18-510 (2 étapes)",
    color: 'bg-lime-50 border-lime-200 text-lime-900',
  },
  {
    id: 'bon_isolation_energies',
    label: "Bon d'Isolation Énergétique (LOTOTO)",
    short: 'IE',
    description: "Isolation énergétique (PCED rattachée ou lignes manuelles)",
    color: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  },
];

export const STATUS_OPTIONS = [
  { id: 'brouillon', label: 'Brouillon' },
  { id: 'soumis', label: 'Soumis' },
  { id: 'revise', label: 'Révisé' },
  { id: 'approuve', label: 'Approuvé' },
  { id: 'rejete', label: 'Rejeté' },
  { id: 'cloture', label: 'Clôturé' },
];

export const ROLES = [
  { id: 'admin_national',                  label: 'Admin National',                              category: 'admin' },
  { id: 'responsable_hse_site',            label: 'Responsable HSE Site',                        category: 'admin' },
  { id: 'charge_travaux_lafarge',          label: 'Chargé de travaux Lafarge',                   category: 'travaux' },
  { id: 'charge_travaux_ee',               label: 'Chargé de travaux EE',                        category: 'travaux' },
  { id: 'signataire_permis_feu',           label: 'Signataire Permis de Feu',                    category: 'signataire' },
  { id: 'signataire_permis_levage',        label: 'Signataire Permis de Levage',                 category: 'signataire' },
  { id: 'signataire_permis_espace_confine',label: 'Signataire Permis Espace Confiné',            category: 'signataire' },
  { id: 'signataire_permis_nacelle',       label: 'Signataire Permis Nacelle',                   category: 'signataire' },
  { id: 'signataire_permis_fouille',       label: 'Signataire Permis de Fouille',                category: 'signataire' },
  { id: 'signataire_permis_bordure_eau',   label: "Signataire Permis Travail en Bordure d'Eau",  category: 'signataire' },
  { id: 'isolateur',                       label: 'Isolateur',                                   category: 'consignation' },
  { id: 'controleur_isolation',            label: "Contrôleur de l'Isolation",                   category: 'consignation' },
];

// Map id → label pour lookup rapide
export const ROLE_LABELS = Object.fromEntries(ROLES.map((r) => [r.id, r.label]));

// Helpers d'autorisation
export const isAdminNational = (user) => !!user && (user.roles || []).includes('admin_national');
export const isResponsableHseSite = (user) => !!user && (user.roles || []).includes('responsable_hse_site');
export const canManageUsers = (user) => isAdminNational(user) || isResponsableHseSite(user);

const COMMON_RISQUES = ['Chute de plain-pied', 'Heurts / Coupures', 'Co-activité', 'Bruit', 'Poussière'];
const COMMON_MESURES = ['Plan de prévention validé', 'Briefing sécurité quotidien', 'Balisage de la zone', 'EPI obligatoires portés'];
const COMMON_EPI = ['Casque', 'Chaussures de sécurité', 'Gants', 'Lunettes', 'Gilet HV'];

export const DEFAULT_RISQUES = {
  autorisation_travail: COMMON_RISQUES,
  permis_feu: ['Incendie / Explosion', 'Projections de scories', 'Brûlures thermiques', 'Émission de fumées toxiques', 'Inflammation matières combustibles'],
  permis_levage: ['Chute de charge', 'Heurts par charge en mouvement', "Renversement de l'engin", 'Rupture d\'élingue'],
  permis_hauteur: ['Chute de hauteur', 'Chute d\'objet sur personnel au sol', 'Mauvais ancrage', 'Mauvais ajustement harnais'],
  permis_nacelle: ['Renversement nacelle', 'Choc contre obstacle aérien', 'Éjection du panier', 'Vent fort > 45 km/h', 'Ligne électrique'],
  permis_espace_confine: ['Asphyxie', 'Atmosphère toxique', 'Atmosphère explosive (ATEX)', 'Ensevelissement', 'Accès difficile / secours'],
  permis_fouille: ['Effondrement / Ensevelissement', 'Réseaux enterrés', 'Chute dans la fouille', 'Co-activité engins'],
  permis_bordure_eau: ['Noyade', 'Hypothermie', 'Chute à l\'eau', 'Co-activité nautique'],
  attestation_consignation_elec: ['Électrocution', 'Arc électrique', 'Brûlure', 'Réénergisation accidentelle'],
  bon_isolation_energies: ['Réénergisation accidentelle', 'Énergie résiduelle', 'Mauvaise identification source', 'Cadenas non posé'],
};

export const DEFAULT_MESURES = {
  autorisation_travail: COMMON_MESURES,
  permis_feu: ['Zone nettoyée (rayon 10m)', 'Extincteur à portée', 'Surveillant feu désigné', 'Surveillance 2h après travaux', 'Couvertures anti-feu'],
  permis_levage: ['Vérification élingues', 'Test à vide', 'Zone évacuée et balisée', 'Élingueur + chef de manœuvre', 'Arrêt si vent > 50 km/h'],
  permis_hauteur: ['Harnais ajusté + double longe', 'Point d\'ancrage vérifié', 'Plan de secours formalisé', 'Kit de sauvetage à proximité', 'Balisage zone au sol'],
  permis_nacelle: ['Notice nacelle consultée', 'VGP < 6 mois OK', 'Surveillant au sol', 'Ancrage permanent dans panier', 'Vent < 45 km/h'],
  permis_espace_confine: ['Contrôle atmosphère (explosimètre)', 'Ventilation forcée', 'Surveillant dédié SST formé', 'Détecteur gaz par intervenant', 'Plan de secours espace confiné'],
  permis_fouille: ['DT-DICT à jour', 'Blindage si profondeur > 1,30m', 'Balisage périmétrique', 'Échelle d\'accès', 'Distance dépôt terres > 1m'],
  permis_bordure_eau: ['Gilet de sauvetage', 'Bouée à proximité', 'Surveillant terrestre', 'Communication radio', 'Pas de travail isolé'],
  attestation_consignation_elec: ['Séparation', 'Condamnation', 'Identification', 'VAT (vérif absence de tension)', 'Mise à la terre + court-circuit'],
  bon_isolation_energies: ['LOCK-OUT physique', 'TAG-OUT étiquetage', 'TRY-OUT vérification', 'Énergies résiduelles dissipées', 'Cadenas individuel par intervenant'],
};

export const DEFAULT_EPI = {
  autorisation_travail: COMMON_EPI,
  permis_feu: ['Casque', 'Lunettes soudeur', 'Tablier cuir', 'Gants anti-feu', 'Vêtement ignifugé'],
  permis_levage: [...COMMON_EPI, 'Harnais (si applicable)'],
  permis_hauteur: ['Casque jugulaire', 'Harnais antichute', 'Double longe + absorbeur', 'Chaussures cramponnées'],
  permis_nacelle: ['Casque jugulaire clipsé', 'Harnais avec longe courte', 'Chaussures de sécurité', 'Gants'],
  permis_espace_confine: ['Casque', 'Détecteur gaz', 'Masque adapté', 'Harnais récupération', 'Lampe frontale ATEX'],
  permis_fouille: COMMON_EPI,
  permis_bordure_eau: ['Casque', 'Gilet de sauvetage', 'Chaussures antidérapantes', 'Combinaison étanche (si froid)'],
  attestation_consignation_elec: ['Casque isolant', 'Gants isolants classés', 'Écran facial arc', 'Tapis isolant', 'VAT'],
  bon_isolation_energies: ['Casque', 'Gants', 'Lunettes', 'Cadenas individuel + clé'],
};

export const statusLabel = (s) => (STATUS_OPTIONS.find((x) => x.id === s) || { label: s }).label;
